import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import CART from '@helpers/cartKeys';
import trackMergeCartModal, { trackMergeCartModalClose } from '@helpers/analyticsHelpers/trackMergeCartModal';
import Modal from '@organisms/Modal/Modal';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import {
  StyledMergeCartButton,
  StyledMergeCartHeading,
  StyledMergeCartText,
} from '@components/organisms/MergeCartModal/MergeCartModal.styles';
import { restoreCartAndCloseOpenOrders } from '@api/interfaces/cartApi';
import { useLoginFlow } from '@root/contexts/LoginFlowContext';
import pushGTMEvent from '@helpers/analyticsHelpers/pushGTMEvent';
import usePersistedCart from '@hooks/usePersistedCart';
import useCustomer from '@hooks/useCustomer';
import useVouchers from '@hooks/useVouchers';
import useCart from '@hooks/useCart';
import useStore from '@hooks/useStore';
import paths, { LoginQueryParams } from '@constants/paths';
import { getPostLoginRedirectPath } from '@constants/protectedRoutes';

const LOGIN_QUERY_PARAMS = ['from', 'name', 'productCode', 'login'];

const MergeCartModal = () => {
  const t = useTranslations('mergeCart');
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [showMergeCartModal, setShowMergeCartModal] = useState(false);
  const { refreshCustomer } = useCustomer();
  const [showOrderClosedModal, setShowOrderClosedModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { persistedCart, clearCache } = usePersistedCart();
  const { refreshVouchers } = useVouchers();
  const { cart, refreshCart } = useCart();
  const { refreshStore } = useStore();
  const { canGoBackAfterLogin } = useLoginFlow();

  const closeModal = () => {
    setShowMergeCartModal(false);
    clearCache();
  };

  // The user should end up where they would have without the merge modal. Logins with a destination have normally
  // been redirected there by usePostLoginRedirect before this modal shows, so the login URL here usually means a
  // flow that stays on the page the login was opened from.
  const navigateAfterMerge = () => {
    if (pathname.startsWith(paths.USER_LOGIN)) {
      const from = searchParams.get('from') as LoginQueryParams | null;
      const redirectPath = getPostLoginRedirectPath(from);

      if (redirectPath) {
        // Safety net in case the login's own redirect hasn't happened. Hard navigation so protected routes are
        // fetched with the new session
        window.location.href = redirectPath;
      } else if (canGoBackAfterLogin()) {
        // Back to the page on the site the login modal was opened from
        router.back();
      } else {
        // Full-page login (reload, new tab or external link): going back could leave the site
        router.push(from === LoginQueryParams.CART_PAGE ? paths.CART : paths.HOME);
      }
      return;
    }

    // Remove login-related query params from URL
    const params = new URLSearchParams(searchParams.toString());
    LOGIN_QUERY_PARAMS.forEach((key) => params.delete(key));
    const queryString = params.toString();
    router.push(queryString ? `${pathname}?${queryString}` : pathname);
  };

  const handleRestoreCart = async (action: string) => {
    setIsSubmitting(true);
    try {
      trackMergeCartModal(action);

      const res = await restoreCartAndCloseOpenOrders(action);
      closeModal();
      setShowOrderClosedModal(res.data.openOrdersClosed);

      await Promise.all([refreshCart(res.data.cart), refreshCustomer(), refreshVouchers(), refreshStore()]);

      navigateAfterMerge();
    } catch (e) {
      console.error(e);
    }
    setIsSubmitting(false);
  };
  /**
   * On close without any action
   */
  const onCloseModalHandler = () => {
    trackMergeCartModalClose();
    handleRestoreCart(CART.KEEP_SESSION);
  };

  useEffect(() => {
    setShowMergeCartModal(true);
    pushGTMEvent({
      event: 'Track',
      category: 'cart_merge_modal',
      action: 'merge_modal_shown',
      label: '',
      value: 0,
    });
  }, []);

  return (
    <>
      {showOrderClosedModal && (
        <Modal
          size="small"
          onClose={() => setShowOrderClosedModal(false)}
          confirmButtonText="Ok"
          onConfirm={() => setShowOrderClosedModal(false)}
        >
          <StyledMergeCartHeading variant="h2" color="black" size="small">
            {t('closedOrder.title')}
          </StyledMergeCartHeading>
          <StyledMergeCartText type="body" color="black">
            {t('closedOrder.text')}
          </StyledMergeCartText>
        </Modal>
      )}

      {showMergeCartModal && persistedCart && (
        <Modal size="medium" hasCloseIcon onClose={onCloseModalHandler}>
          <div>
            <div>
              <StyledMergeCartHeading variant="h2" color="black" size="small">
                {t('title')}
              </StyledMergeCartHeading>
              <StyledMergeCartText type="body" color="black">
                {t('text.savedCart', {
                  count: persistedCart.totalPersistedCartProducts,
                  date: persistedCart.persistedCartFormattedDate ?? '',
                })}
              </StyledMergeCartText>
              <StyledMergeCartText type="body" color="black">
                {t('text.actions')}
              </StyledMergeCartText>
            </div>

            {!!cart && (
              <div>
                <StyledMergeCartButton onClick={() => handleRestoreCart(CART.KEEP_SESSION)} isSubmitting={isSubmitting}>
                  {cart.totalUnitCount > 1
                    ? t('actions.continueWithSession', { count: cart.totalUnitCount })
                    : t('actions.continueWithOneItemSession', { count: cart.totalUnitCount })}
                </StyledMergeCartButton>
                <StyledMergeCartButton
                  onClick={() => handleRestoreCart(CART.KEEP_PERSISTENT)}
                  isSubmitting={isSubmitting}
                >
                  {persistedCart.totalPersistedCartProducts > 1
                    ? t('actions.continueWithPersistent', { count: persistedCart.totalPersistedCartProducts })
                    : t('actions.continueWithOneItemPersistent', { count: persistedCart.totalPersistedCartProducts })}
                </StyledMergeCartButton>
                <StyledMergeCartButton onClick={() => handleRestoreCart(CART.MERGE)} isSubmitting={isSubmitting}>
                  {t('actions.continueWithMerged', {
                    count: persistedCart.totalPersistedCartProducts + cart.totalUnitCount,
                  })}
                </StyledMergeCartButton>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default MergeCartModal;
